import { component } from 'picoapp';
import cache from '~/js/utils/indexeddb-cache';

export default component(async (node) => {
  const formContainer = node.querySelector('[data-form-container]');
  const productTitle = node.querySelector('[data-product-title]');
  const imageContainer = node.querySelector('[data-swiper-main]');
  const maxRetries = 3;

  /**
   * Fetch product JSON data with retry logic
   */
  // eslint-disable-next-line consistent-return
  const fetchProductData = async (handle) => {
    // Try cache first
    try {
      const cachedData = await cache.getProduct(handle);
      if (cachedData) {
        return cachedData;
      }
    } catch (error) {
      // Cache read failed, continue to fetch from server
    }

    // Fetch from server with retry logic
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await fetch(`/products/${handle}.js`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        // eslint-disable-next-line no-await-in-loop
        const productData = await response.json();

        // Cache the result
        try {
          // eslint-disable-next-line no-await-in-loop
          await cache.setProduct(handle, productData);
        } catch (error) {
          // Cache write failed, continue without caching
        }

        return productData;
      } catch (error) {
        // Attempt failed, will retry

        if (attempt === maxRetries) {
          throw new Error(`Failed to fetch product ${handle} after ${maxRetries} attempts: ${error.message}`);
        }

        // Exponential backoff: wait 1s, 2s, 4s
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, 1000 * (2 ** attempt));
        });
      }
    }
  };

  /**
   * Fetch product form HTML with retry logic
   */
  // eslint-disable-next-line consistent-return
  const fetchProductForm = async (handle) => {
    // Try cache first
    try {
      const cachedHtml = await cache.getHtmlPartial(handle);
      if (cachedHtml) {
        return cachedHtml;
      }
    } catch (error) {
      // Cache read failed, continue to fetch from server
    }

    // Create a temporary form to get the form HTML
    const tempForm = document.createElement('form');
    tempForm.method = 'POST';
    tempForm.action = '/cart/add';
    tempForm.style.display = 'none';

    const handleInput = document.createElement('input');
    handleInput.type = 'hidden';
    handleInput.name = 'handle';
    handleInput.value = handle;

    tempForm.appendChild(handleInput);
    document.body.appendChild(tempForm);

    // Fetch from server with retry logic
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const formData = new FormData();
        formData.append('handle', handle);

        // Try to fetch using section rendering first
        let response;
        try {
          // First try to get the STL form partial specifically
          // eslint-disable-next-line no-await-in-loop
          response = await fetch(`/products/${handle}?section_id=stl-product-form-fetch`, {
            method: 'GET',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
          });

          if (!response.ok) {
            throw new Error('Section fetch failed, falling back to full page');
          }
        } catch (error) {
          // Fallback to full product page
          // eslint-disable-next-line no-await-in-loop
          response = await fetch(`/products/${handle}`, {
            method: 'GET',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
          });
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        // eslint-disable-next-line no-await-in-loop
        let formHtml = await response.text();

        // If the response doesn't contain our partial, fall back to generating form HTML
        if (!formHtml.includes('stl-product-form-content')) {
          // Response doesn't contain partial, generating fallback
          // eslint-disable-next-line no-await-in-loop
          const productData = await fetchProductData(handle);

          // Define renderProductFormFromData inline for now
          const renderProductFormFromData = async (data) => {
            const variant = data.variants?.[0];
            if (!variant) return '';

            const formatPrice = (price) => new Intl.NumberFormat('da-DK', {
              style: 'currency',
              currency: 'DKK',
            }).format(price / 100);

            // Generate basic variant options if product has multiple variants
            let variantOptionsHtml = '';
            if (data.variants && data.variants.length > 1) {
              variantOptionsHtml = `
                <div class="stl-product-options">
                  <select name="id" id="stl-product-${data.id}" class="hidden" data-option-selector>
                    ${data.variants.map((v) => `<option value="${v.id}" ${v.id === variant.id ? 'selected' : ''}>${v.title} - ${formatPrice(v.price)}</option>`).join('')}
                  </select>
                  
                  ${data.options ? data.options.map((option, optionIndex) => {
    const isSize = option.name.toLowerCase() === 'size' || option.name.toLowerCase() === 'størrelse';
    return `
                    <div class="stl-option-group mb-4" data-option-group="${option.name}">
                      <div class="flex items-center justify-between mb-2">
                        <label class="font-heading text-sm font-semibold" for="option-${data.id}-${optionIndex + 1}">
                          ${option.name}
                        </label>
                        ${isSize ? `
                          <button
                            type="button"
                            data-action="open-size-guide"
                            data-product-handle="${data.handle}"
                            data-option-name="${option.name}"
                            class="items-center text-xs underline flex font-bold font-heading"
                          >
                            <img src="//vibholm.dk/cdn/shop/files/size-guide-icon.svg?v=18166914385422241055" alt="Størrelsesguide" class="mr-3" width="16" height="16">
                            ${window.theme?.strings?.sizeGuide || 'Size Guide'}
                          </button>
                        ` : ''}
                      </div>
                      
                      <select 
                        id="option-${data.id}-${optionIndex + 1}"
                        name="options[${option.name}]" 
                        class="w-full px-7 py-4 border border-warm-gray-200 rounded font-heading text-sand-800 text-base leading-tight"
                        data-option-selector
                        data-option-name="${option.name}"
                        data-option-index="${optionIndex + 1}"
                      >
                        ${option.values.map((value) => `
                          <option value="${value}" ${variant[`option${optionIndex + 1}`] === value ? 'selected' : ''}>
                            ${value}
                          </option>
                        `).join('')}
                      </select>
                    </div>
                  `;
  }).join('') : ''}
                </div>
              `;
            }

            return `
              <div class="stl-product-form-content" data-product-id="${data.id}">
                <form data-component="stl-product-form" data-product-handle="${data.handle}" data-action="submit">
                  <input type="hidden" name="id" value="${variant.id}" data-variant-id>
                  
                  <div class="stl-form-header mb-4">
                    <div class="flex justify-between items-center">
                      <span class="font-heading text-[2rem] font-bold" data-product-price>
                        ${formatPrice(variant.price)}
                      </span>
                      <button 
                        type="button" 
                        class="font-heading text-[1.6rem] font-semibold underline text-[#BE8C2A]"
                        data-action="open-product-modal"
                        data-product-handle="${data.handle}"
                      >
                        About Product
                      </button>
                    </div>
                  </div>

                  <div class="stl-form-options">
                    ${variantOptionsHtml}
                    
                    <div class="stl-form-actions mt-6">
                      <button
                        class="w-full py-5 text-base button button--buy"
                        data-add-to-cart-btn
                        data-update="submit-button"
                        ${!variant.available ? 'disabled' : ''}
                      >
                        ${variant.available ? '🛒 Add to cart' : 'Sold out'}
                      </button>
                    </div>
                  </div>

                  <script type="application/json" data-product-data>
                    ${JSON.stringify(data)}
                  </script>
                </form>
              </div>
            `;
          };

          // eslint-disable-next-line no-await-in-loop
          formHtml = await renderProductFormFromData(productData);
        }

        // Cache the result
        try {
          // eslint-disable-next-line no-await-in-loop
          await cache.setHtmlPartial(handle, formHtml);
        } catch (error) {
          // Cache write failed, continue without caching
        }

        return formHtml;
      } catch (error) {
        // Attempt failed, will retry

        if (attempt === maxRetries) {
          throw new Error(`Failed to fetch form for ${handle} after ${maxRetries} attempts: ${error.message}`);
        }

        // Exponential backoff: wait 1s, 2s, 4s
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, 1000 * (2 ** attempt));
        });
      }
    }

    // Cleanup
    document.body.removeChild(tempForm);
  };

  /**
   * Update product images in the main swiper
   */
  const updateProductImages = (productData) => {
    if (!imageContainer || !productData.images) return;

    const swiperWrapper = imageContainer.querySelector('.swiper-wrapper');
    if (!swiperWrapper) return;

    // Generate new slides HTML
    const slidesHtml = productData.images.map((imageUrl) => {
      // Add https: protocol if missing
      const fullImageUrl = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;

      return `
        <div class="swiper-slide">
          <div class="w-full">
            <div class="aspect-w-1 aspect-h-1">
              <img 
                src="${fullImageUrl}" 
                alt="${productData.title}"
                class="w-full h-full object-cover"
                loading="lazy"
              >
            </div>
          </div>
        </div>
      `;
    }).join('');

    // Update the wrapper
    swiperWrapper.innerHTML = slidesHtml;

    // Update slide count
    const slideCountEl = imageContainer.querySelector('[data-image-slide-index]');
    const slideCountTotal = imageContainer.querySelector('.swiper-pagination');

    if (slideCountEl) {
      slideCountEl.textContent = '1';
    }

    if (slideCountTotal) {
      slideCountTotal.textContent = `1/${productData.images.length}`;
    }

    // Dispatch event to reset and update the main Swiper
    document.dispatchEvent(new CustomEvent('stl:swiper:update', {
      detail: { imageCount: productData.images.length },
    }));

    // Dispatch event to update the STL lightbox
    document.dispatchEvent(new CustomEvent('stl:lightbox:update', {
      detail: { productData },
    }));
  };

  /**
   * Show loading skeleton
   */
  const showLoading = () => {
    if (!formContainer) return;

    formContainer.innerHTML = `
      <div class="stl-form-loading">
        <div class="animate-pulse space-y-4">
          <div class="h-8 bg-gray-300 rounded w-3/4"></div>
          <div class="flex justify-between">
            <div class="h-8 bg-gray-300 rounded w-24"></div>
            <div class="h-6 bg-gray-300 rounded w-32"></div>
          </div>
          <div class="space-y-3">
            <div class="h-4 bg-gray-300 rounded w-20"></div>
            <div class="h-12 bg-gray-300 rounded"></div>
          </div>
          <div class="h-12 bg-gray-300 rounded"></div>
        </div>
      </div>
    `;
  };

  /**
   * Show error state
   */
  const showError = (message) => {
    if (!formContainer) return;

    formContainer.innerHTML = `
      <div class="stl-form-error p-4 bg-red-50 border border-red-200 rounded">
        <p class="text-red-600 font-semibold">Error loading product</p>
        <p class="text-red-500 text-sm mt-1">${message}</p>
        <button 
          class="mt-3 text-sm bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          onclick="location.reload()"
        >
          Retry
        </button>
      </div>
    `;
  };

  /**
   * Fetch product metafields by looking for them in the form partial response
   * We'll modify the form partial to include metafields data
   */
  const extractMetafieldsFromFormHtml = (formHtml) => {
    try {
      // Look for metafields script tag in the form HTML
      const metafieldsMatch = formHtml.match(/<script[^>]*data-metafields[^>]*>(.*?)<\/script>/s);
      if (metafieldsMatch) {
        const metafieldsJson = metafieldsMatch[1].trim();
        return JSON.parse(metafieldsJson);
      }
      return null;
    } catch (error) {
      console.warn('STL Fetcher: Error parsing metafields from form HTML:', error);
      return null;
    }
  };

  /**
   * Generate linked products HTML manually if the form doesn't include it
   */
  const generateLinkedProductsSection = (productData, metafields) => {
    if (!metafields?.functional?.linked_products) {
      return '';
    }

    const linkedProducts = metafields.functional.linked_products;
    const currentSku = productData.variants[0]?.sku || '';

    // Parse the linked products - it's a multi-line text field
    const skuList = linkedProducts.split('\n').map((sku) => sku.trim()).filter((sku) => sku);

    return `
      <div 
        class="stl-linked-products-container" 
        data-component="stl-linked-products"
        data-product-id="${productData.id}"
        data-current-sku="${currentSku}"
      >
        <script type="application/json" data-sku-list>${JSON.stringify(skuList)}</script>
        
        <div class="stl-linked-products py-4">
          <select 
            id="linked-products-${productData.id}"
            class="w-full px-7 py-4 border border-warm-gray-200 rounded font-heading text-sand-800 text-base leading-tight"
            data-linked-product-selector
          >
            <option value="" data-placeholder>Loading...</option>
          </select>
          <div class="hidden mt-2" data-linked-loading>
            <div class="animate-pulse">
              <div class="h-4 bg-gray-300 rounded w-24"></div>
            </div>
          </div>
          <div class="hidden text-red-600 text-sm mt-2" data-linked-error>
            Failed to load linked products
          </div>
        </div>
      </div>
    `;
  };

  /**
   * Handle product change events
   */
  const handleProductChange = async (event) => {
    const { handle } = event.detail;

    if (!handle) {
      return;
    }

    try {
      showLoading();

      // Fetch product data and form HTML

      const [productData, formHtml] = await Promise.all([
        fetchProductData(handle),
        fetchProductForm(handle),
      ]);

      // Extract metafields from the form HTML
      const metafields = extractMetafieldsFromFormHtml(formHtml);

      // Update form container
      if (formContainer) {
        formContainer.innerHTML = formHtml;
        formContainer.dataset.currentHandle = handle;

        // Check if linked products component is in the new HTML
        const linkedProductsContainer = formContainer.querySelector('[data-component="stl-linked-products"]');

        // If not found, but we have metafields, generate the section
        if (!linkedProductsContainer && metafields?.functional?.linked_products) {
          const linkedProductsHtml = generateLinkedProductsSection(productData, metafields);

          // Insert after the price section
          const formOptionsContainer = formContainer.querySelector('.stl-form-options');
          if (formOptionsContainer) {
            formOptionsContainer.insertAdjacentHTML('afterbegin', linkedProductsHtml);
          }
        }
      }

      // Update product images

      updateProductImages(productData);

      productTitle.innerText = productData.title;

      // Trigger remounting for new components

      document.dispatchEvent(new Event('app:remount'));
    } catch (error) {
      console.error('STL Fetcher: Failed to update product:', error);
      showError(error.message);
    }
  };

  /**
   * Initialize with current product images for lightbox
   */
  const initCurrentProduct = () => {
    // Get initial product data from the form container
    const currentForm = formContainer?.querySelector('[data-product-data]');
    if (currentForm) {
      try {
        const productData = JSON.parse(currentForm.textContent);

        // Dispatch initial lightbox update
        document.dispatchEvent(new CustomEvent('stl:lightbox:update', {
          detail: { productData },
        }));
      } catch (error) {
        console.warn('STL Fetcher: Failed to parse initial product data:', error);
      }
    }
  };

  // Initialize with current product
  setTimeout(initCurrentProduct, 100);

  // Listen for product change events
  document.addEventListener('stl:product:change', handleProductChange);

  // Cleanup
  return () => {
    document.removeEventListener('stl:product:change', handleProductChange);
  };
});
